import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaSpinner, FaPlus } from 'react-icons/fa';
import toursService from '../../services/toursService';
import TourModal from './TourModal';

const TourCard = ({ tour, onClick }) => {
  // Format price with currency
  const formatPrice = (price) => {
    if (!price) return 'Price unavailable';
    return `${price.currency || '$'}${price.amount?.toFixed(0) || '0'}`;
  };

  return (
    <div 
      className="rounded-lg overflow-hidden shadow-sm bg-white hover:shadow-md transition-all duration-300 cursor-pointer h-full transform hover:scale-[1.02]"
      onClick={() => onClick(tour)}
    >
      <div className="relative h-32 overflow-hidden">
        <img 
          src={tour.images?.main || tour.images?.gallery?.[0] || 'https://placehold.co/800x600/1A1B1E/ffffff?text=No+Image'} 
          alt={tour.name} 
          className="w-full h-full object-cover"
        />
        {tour.price && (
          <div className="absolute bottom-2 left-2 bg-white text-gray-900 px-2 py-1 rounded-md text-sm font-medium shadow-sm">
            {formatPrice(tour.price)}
          </div>
        )}
      </div>
      <div className="p-2">
        <h3 className="text-sm font-medium text-gray-900 truncate">{tour.name}</h3>
      </div>
    </div>
  );
};

// Load More Button component
const LoadMoreButton = ({ onClick, isLoading }) => {
  return (
    <div 
      onClick={isLoading ? undefined : onClick}
      className={`flex flex-col items-center justify-center h-full cursor-pointer transition-all duration-300 ${isLoading ? 'opacity-70' : 'hover:scale-105'}`}
    >
      <div className={`rounded-full p-2 shadow-md mb-1 ${isLoading ? 'bg-gray-600' : 'bg-indigo-600'}`}>
        {isLoading ? (
          <FaSpinner className="animate-spin text-white text-sm" />
        ) : (
          <FaPlus className="text-white text-sm" />
        )}
      </div>
      <p className="text-xs font-medium text-white text-shadow">
        {isLoading ? 'Loading' : 'More'}
      </p>
    </div>
  );
};

const MinimalTourSlider = ({ keywords = [] }) => {
  const [tours, setTours] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const sliderRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedTour, setSelectedTour] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchTours = async (pageToFetch = 1, append = false) => {
    try {
      if (pageToFetch === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      setError(null);
      
      const params = {
        page: pageToFetch,
        limit: 10,
        ...(keywords && keywords.length > 0 && { search: keywords.join(' ') })
      };
      
      const response = await toursService.fetchTours(params);
      
      if (append) {
        setTours(prev => [...prev, ...response.tours]);
      } else {
        setTours(response.tours);
      }
      
      // Check if there are more tours to load
      setHasMore(response.pagination?.total > pageToFetch * response.pagination?.limit);
      
    } catch (err) {
      console.error('Error fetching tours:', err);
      setError('Failed to load tours');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchTours(1, false);
  }, [keywords]);

  useEffect(() => {
    const updateMaxScroll = () => {
      if (sliderRef.current) {
        setMaxScroll(sliderRef.current.scrollWidth - sliderRef.current.clientWidth);
      }
    };

    updateMaxScroll();
    
    // Update maxScroll when window is resized
    window.addEventListener('resize', updateMaxScroll);
    return () => window.removeEventListener('resize', updateMaxScroll);
  }, [tours]);

  const scrollLeft = () => {
    if (sliderRef.current) {
      // Scroll by approximately one card width
      const newPosition = Math.max(0, scrollPosition - sliderRef.current.clientWidth / 2.5);
      sliderRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
      setScrollPosition(newPosition);
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      // Scroll by approximately one card width
      const newPosition = Math.min(maxScroll, scrollPosition + sliderRef.current.clientWidth / 2.5);
      sliderRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
      setScrollPosition(newPosition);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      setScrollPosition(sliderRef.current.scrollLeft);
    }
  };

  const loadMoreTours = async () => {
    if (isLoadingMore || !hasMore) return;
    
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchTours(nextPage, true);
    
    // Scroll to show the newly loaded items
    setTimeout(() => {
      if (sliderRef.current) {
        const newPosition = scrollPosition + 100; // Scroll a bit to show new content
        sliderRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleTourClick = (tour) => {
    console.log('Tour clicked:', tour);
    
    // Create a copy of the tour to avoid mutating the original
    const tourToShow = { ...tour };
    
    // For Viator tours, ensure they have an _id
    if (!tourToShow._id && tourToShow.source === 'viator') {
      console.log('Adding ID to Viator tour');
      tourToShow._id = tourToShow.viatorData?.productCode || `viator-${Date.now()}`;
      console.log('Generated Viator tour ID:', tourToShow._id);
    } else if (!tourToShow._id) {
      console.error('Tour is missing _id:', tourToShow);
      return;
    }
    
    setSelectedTour(tourToShow);
    setModalOpen(true);
  };

  const closeModal = () => {
    console.log('Closing modal');
    setModalOpen(false);
    // Small delay before clearing the selected tour
    setTimeout(() => {
      setSelectedTour(null);
    }, 300);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-4">
        <FaSpinner className="animate-spin text-indigo-600 text-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center py-2 text-sm">
        {error}
      </div>
    );
  }

  if (!tours || tours.length === 0) {
    return (
      <div className="text-gray-500 text-center py-2 text-sm">
        No tours available for this search
      </div>
    );
  }

  return (
    <div className="relative py-2">
      <h3 className="text-sm font-medium text-gray-500 mb-3">Recommended Tours</h3>
      <div className="relative overflow-hidden">
        <div 
          ref={sliderRef}
          className="flex overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1"
          onScroll={handleScroll}
        >
          {tours.map((tour) => (
            <div 
              key={tour._id} 
              className="flex-none mr-3 w-[48%] sm:w-[32%] md:w-[30%] lg:w-[40%]"
            >
              <TourCard tour={tour} onClick={handleTourClick} />
            </div>
          ))}
          
          {/* Load More Button */}
          {hasMore && (
            <div 
              className="flex-none flex items-center self-center justify-center mr-3 ml-1 h-32"
              style={{ minWidth: '60px' }}
            >
              <LoadMoreButton onClick={loadMoreTours} isLoading={isLoadingMore} />
            </div>
          )}
        </div>
        
        {tours.length > 3 && (
          <>
            {scrollPosition > 5 && (
              <button 
                onClick={scrollLeft}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 shadow-md rounded-full p-1.5 z-10 hover:bg-opacity-100 transition-all duration-200"
                aria-label="Previous"
              >
                <FaChevronLeft className="text-gray-600" />
              </button>
            )}
            
            {scrollPosition < maxScroll - 5 && (
              <button 
                onClick={scrollRight}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-70 shadow-md rounded-full p-1.5 z-10 hover:bg-opacity-100 transition-all duration-200"
                aria-label="Next"
              >
                <FaChevronRight className="text-gray-600" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Tour Modal */}
      {selectedTour && (
        <TourModal 
          tourId={selectedTour._id}
          source={selectedTour.source || 'local'}
          isOpen={modalOpen}
          onClose={closeModal}
          fallbackTourData={selectedTour}
        />
      )}
    </div>
  );
};

export default MinimalTourSlider; 